import type { FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import type { ThrowOnQueryError$key } from './__generated__/ThrowOnQueryError.graphql.ts';

/**
 * Use me to read QueryError
 * and throw a formatted error when needed
 *
 * @example
 *  query fooQuery {
 *      customerService {
 *          entitlementById {
 *              __typename
 *              ... on CustomerServiceEntitlement {
 *                  ...fooStuff
 *              }
 *              ... on QueryError {
 *                  ...ThrowOnQueryError
 *              }
 *          }
 *      }
 *  }
 *
 *  if (entitlementById.__typename !== 'CustomerServiceEntitlement') {
 *      return (
 *          <ThrowOnQueryError
 *              operationName="fooQuery"
 *              errorRef={entitlementById.__typename === 'QueryError' ? entitlementById : undefined}
 *          />
 *      );
 *  }
 */
export const ThrowOnQueryError: FC<{
	operationName: string;
	errorRef?: ThrowOnQueryError$key | null;
}> = ({ operationName, errorRef }) => {
	if (!errorRef) {
		throw new Error(`${operationName}: unexpected typename returned`);
	}

	const { identifier, message, extensions } = useFragment(
		graphql`
			fragment ThrowOnQueryError on QueryError {
				identifier
				message
				extensions {
					# eslint-disable-next-line @atlassian/relay/unused-fields
					statusCode
					# eslint-disable-next-line @atlassian/relay/unused-fields
					errorType
				}
			}
		`,
		errorRef,
	);

	throw new Error(
		`${operationName}: QueryError; ${JSON.stringify({
			identifier,
			message,
			extensions,
		})}`,
	);
};
